import React, { FormEvent } from "react";
import { matchCase } from "src/utils";
import {
  MAX_VENDOR_EMAILS,
  VENDOR_LOCATIONS,
  INCO_TERMS_OPTIONS,
  COUNTRY_OF_EXPORT_OPTIONS,
  COLLECTION_TYPE_OPTIONS
} from "src/constants";
import { CreateVendorFormData, CreateVendorFormDataKey } from "src/types";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import {
  CollectionOrderVendorToolTip,
  CustomEmailGreetingToolTip,
  PortalAccessVendorToolTip,
  PortalOrderVendorToolTip,
  IsProFormaVendorToolTip,
  FileTypeVendorToolTip,
  DirectInjectionToAAToolTip
} from "src/components/tooltips/vendorsToolTips";
import { SubmissionTooltip } from "src/components/tooltips";
import {
  OptionSelector,
  TextInputBoxList,
  TextInputBox,
  SelectInputBox,
  MultiSelectInputBox
} from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { ShipmentDetailsForm } from "./ShipmentDetailsForm";

interface CreateVendorFormFormProps {
  formData: CreateVendorFormData;
  validationErrors?: ValidationErrors<CreateVendorFormDataKey>;
  formLvlErrorMessage?: string;
  isFormComplete: boolean;
  handleInputChange: (field: CreateVendorFormDataKey, value: CreateVendorFormData[keyof CreateVendorFormData]) => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

const CreateVendorForm: React.FC<CreateVendorFormFormProps> = ({
  formData,
  handleInputChange,
  handleSubmit,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleEmailsChange: React.Dispatch<React.SetStateAction<string[]>> = (emails) =>
    handleInputChange("emails", emails as string[]);

  const handleAddEmptyEmail = () =>
    formData.emails.length < MAX_VENDOR_EMAILS && handleInputChange("emails", [...formData.emails, ""]);

  const handleShipmentDetailsChange = (field: keyof typeof formData.shipmentDetails, value: any) => {
    handleInputChange("shipmentDetails", { ...formData.shipmentDetails, [field]: value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Create Vendor</h1>
            <p className="text-primary-100 mt-2 text-lg">Add a new vendor to the system</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-8">
              {/* Basic Information Card */}
              <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                <CollapsibleSection
                  initiallyOpen={true}
                  title={
                    <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h2M9 7h6m-6 4h6m-2 4h2"
                        />
                      </svg>
                      Basic Information
                    </h3>
                  }
                >
                  <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Vendor Name</label>
                      <TextInputBox
                        id="vendor"
                        onInputChange={(value: string) => handleInputChange("vendorName", value)}
                        inputValue={formData.vendorName}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("vendorName")} />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Location</label>
                      <TextInputBox
                        id="location"
                        onInputChange={(value: string) => handleInputChange("location", value)}
                        inputValue={formData.location}
                        onFocusChange={() =>
                          handleInputChange("location", matchCase(formData.location, VENDOR_LOCATIONS))
                        }
                        maxLength={30}
                        dropDownOptions={VENDOR_LOCATIONS}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("location")} />
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Email Addresses</label>
                      <TextInputBoxList
                        id="vendor-eml"
                        inputValueList={formData.emails}
                        onInputChange={handleEmailsChange}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("emails")} />
                      <button
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleAddEmptyEmail}
                        type="button"
                        disabled={formData.emails.length >= MAX_VENDOR_EMAILS}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Add Email
                      </button>
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="flex items-center text-sm font-medium text-secondary-700 mb-2">
                        Custom Email Greeting
                        <span className="text-secondary-500 text-xs ml-2">(optional)</span>
                        <div className="ml-2">
                          <CustomEmailGreetingToolTip />
                        </div>
                      </label>
                      <TextInputBox
                        id="custom-email-greeting"
                        onInputChange={(value: string) => handleInputChange("emailGreeting", value)}
                        inputValue={formData.emailGreeting}
                        maxLength={10}
                      />
                      <ValidationErrorMessage
                        message={validationErrors?.getErrorMessageByFormDataKey("emailGreeting")}
                      />
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="flex items-center text-sm font-medium text-secondary-700 mb-2">
                        Invoice Name
                        <span className="text-secondary-500 text-xs ml-2">(optional)</span>
                      </label>
                      <TextInputBox
                        id="invoice-name"
                        onInputChange={(value: string) => handleInputChange("invoiceName", value)}
                        inputValue={formData.invoiceName}
                        maxLength={100}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("invoiceName")} />
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="flex items-center text-sm font-medium text-secondary-700 mb-2">
                        Payment Terms
                        <span className="text-secondary-500 text-xs ml-2">(optional)</span>
                      </label>
                      <TextInputBox
                        id="payment-terms"
                        onInputChange={(value: string) => handleInputChange("paymentTerms", value)}
                        inputValue={formData.paymentTerms}
                        maxLength={10}
                      />
                      <ValidationErrorMessage
                        message={validationErrors?.getErrorMessageByFormDataKey("paymentTerms")}
                      />
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Inco Terms</label>
                      <SelectInputBox
                        id="inco-terms"
                        value={formData.incoTerms}
                        options={INCO_TERMS_OPTIONS}
                        onChange={(value: string) => handleInputChange("incoTerms", value)}
                        placeholder="Select Inco Terms"
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("incoTerms")} />
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Country of Export</label>
                      <MultiSelectInputBox
                        id="country-of-export"
                        values={formData.countryOfExport}
                        options={COUNTRY_OF_EXPORT_OPTIONS}
                        onChange={(values: string[]) => handleInputChange("countryOfExport", values)}
                        placeholder="Select countries..."
                      />
                      <ValidationErrorMessage
                        message={validationErrors?.getErrorMessageByFormDataKey("countryOfExport")}
                      />
                    </div>
                  </div>
                </CollapsibleSection>
              </div>

              {/* Configuration Settings Card */}
              <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                <CollapsibleSection
                  initiallyOpen={true}
                  title={
                    <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Configuration Settings
                    </h3>
                  }
                >
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    <div className="space-y-3">
                      <label className="flex items-center text-sm font-medium text-secondary-700">
                        Portal Order Vendor
                        <div className="ml-2">
                          <PortalOrderVendorToolTip />
                        </div>
                      </label>
                      <OptionSelector
                        options={["NO", "YES"]}
                        onSelect={(option: string | undefined) =>
                          handleInputChange("portalOrderVendor", option === "YES")
                        }
                        value={formData.portalOrderVendor ? "YES" : "NO"}
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center text-sm font-medium text-secondary-700">
                        Collection Order Vendor
                        <div className="ml-2">
                          <CollectionOrderVendorToolTip />
                        </div>
                      </label>
                      <OptionSelector
                        options={["NO", "YES"]}
                        onSelect={(option: string | undefined) =>
                          handleInputChange("collectionOrderVendor", option === "YES")
                        }
                        value={formData.collectionOrderVendor ? "YES" : "NO"}
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center text-sm font-medium text-secondary-700">
                        Collection Type
                      </label>
                      <SelectInputBox
                        id="collection-type"
                        value={formData.collectionType}
                        options={COLLECTION_TYPE_OPTIONS}
                        onChange={(value: string) => handleInputChange("collectionType", value)}
                        placeholder="Select Collection Type"
                        disabled={!formData.collectionOrderVendor}
                      />
                      <ValidationErrorMessage
                        message={validationErrors?.getErrorMessageByFormDataKey("collectionType")}
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center text-sm font-medium text-secondary-700">
                        Pro Forma Vendor
                        <div className="ml-2">
                          <IsProFormaVendorToolTip />
                        </div>
                      </label>
                      <OptionSelector
                        options={["NO", "YES"]}
                        onSelect={(option: string | undefined) => handleInputChange("isProForma", option === "YES")}
                        value={formData.isProForma ? "YES" : "NO"}
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center text-sm font-medium text-secondary-700">
                        Portal Access
                        <div className="ml-2">
                          <PortalAccessVendorToolTip />
                        </div>
                      </label>
                      <OptionSelector
                        options={["NO", "YES"]}
                        onSelect={(option: string | undefined) => handleInputChange("portalAccess", option === "YES")}
                        value={formData.portalAccess ? "YES" : "NO"}
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center text-sm font-medium text-secondary-700">
                        Direct Injection to Active Ants
                        <div className="ml-2">
                          <DirectInjectionToAAToolTip />
                        </div>
                      </label>
                      <OptionSelector
                        options={["NO", "YES"]}
                        onSelect={(option: string | undefined) => handleInputChange("isDirectToAA", option === "YES")}
                        value={formData.isDirectToAA ? "YES" : "NO"}
                      />
                    </div>

                    <div className="space-y-3 md:col-span-2 xl:col-span-1">
                      <label className="flex items-center text-sm font-medium text-secondary-700">
                        File Type Preference
                        <div className="ml-2">
                          <FileTypeVendorToolTip />
                        </div>
                      </label>
                      <OptionSelector
                        options={["PDF", "CSV"]}
                        onSelect={(option: string | undefined) =>
                          handleInputChange("fileType", option as "CSV" | "PDF")
                        }
                        value={formData.fileType}
                      />
                    </div>
                  </div>
                </CollapsibleSection>
              </div>

              {/* Shipment Details Section */}
              {formData.collectionOrderVendor && (
                <CollapsibleSection
                  title={
                    <h3 className="text-lg font-semibold text-warning-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-warning-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        />
                      </svg>
                      Shipment Details (Optional)
                    </h3>
                  }
                >
                  <ShipmentDetailsForm
                    shipmentDetails={formData.shipmentDetails}
                    validationErrors={validationErrors}
                    onFieldChange={handleShipmentDetailsChange}
                    isUpdateMode={false}
                  />
                </CollapsibleSection>
              )}
            </div>

            <div className="my-6">
              {validationErrors && validationErrors.length > 0 && (
                <div className="mb-6">
                  <ValidationErrorMessage message={validationErrors[0].message} />
                </div>
              )}
            </div>

            {/* Form Level Error Message */}
            <div className="mt-6">
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-start items-center pt-6 border-t border-secondary-200">
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <button
                  className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    !isFormComplete
                      ? "bg-secondary-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-success-600 to-success-700 hover:from-success-700 hover:to-success-800 focus:ring-success-500 shadow-lg hover:shadow-xl"
                  }`}
                  disabled={!isFormComplete}
                  type="submit"
                >
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Create Vendor
                  </span>
                </button>
              </div>

              <div className="flex-shrink-0">
                <SubmissionTooltip formState={!isFormComplete ? "INCOMPLETE" : "NO_CHANGE"} />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CreateVendorForm };
